// UniversPage — Page de sélection des 21 univers AbracadaWorld
// Design pixel-perfect pour mobile Samsung Galaxy A54 (390×844px)
// Ne toucher à rien d'autre sur la plateforme

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, User, Check } from 'lucide-react';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import MainNavigation from '../components/layout/MainNavigation';

import { API_URL } from '../config/api';

// ═══ DONNÉES DES UNIVERS ═══
const UNIVERS_BIENTOT = [
  { id: 'abracadahome', name: 'AbracadaHome', desc: 'Décoration & Maison', icon: '🏠', iconBg: '#E6F1FB', nameColor: '#185FA5' },
  { id: 'abracadamatch', name: 'AbracadaMatch', desc: 'Rencontres certifiées', icon: '❤️', iconBg: '#FCEBEB', nameColor: '#B33535' },
  { id: 'abracadatech', name: 'AbracadaTech', desc: 'High-Tech & Mobile', icon: '💻', iconBg: '#E6F1FB', nameColor: '#093785' },
  { id: 'abracadagreen', name: 'AbracadaGreen', desc: 'Écologie & Énergie', icon: '🌿', iconBg: '#EAF3DE', nameColor: '#3B6D11' },
  { id: 'abracadaevent', name: 'AbracadaEvent', desc: 'Événementiel & Fêtes', icon: '🎉', iconBg: '#FAECE7', nameColor: '#A83D1C' },
  { id: 'abracadasecret', name: 'AbracadaSecret', desc: 'Adultes 18+', icon: '🔐', iconBg: '#FBEAF0', nameColor: '#72243E' },
  { id: 'abracadatravel', name: 'AbracadaTravel', desc: 'Voyage & Expériences', icon: '✈️', iconBg: '#E6F1FB', nameColor: '#0D6EAF' },
  { id: 'abracadacar', name: 'AbracadaCar', desc: 'Automobile & Moto', icon: '🚗', iconBg: '#EEEDFE', nameColor: '#534AB7' },
  { id: 'abracadarealty', name: 'AbracadaRealty', desc: 'Immobilier Mondial', icon: '🏡', iconBg: '#EEEDFE', nameColor: '#534AB7' },
];

const UNIVERS_2027 = [
  { id: 'abracadafood', name: 'AbracadaFood', desc: 'Alimentation & Resto', icon: '🍽️', iconBg: '#FFF3EC' },
  { id: 'abracadasport', name: 'AbracadaSport', desc: 'Sport & Fitness', icon: '⚽', iconBg: '#FAECE7' },
  { id: 'abracadaboat', name: 'AbracadaBoat', desc: 'Nautisme & Mer', icon: '⛵', iconBg: '#E1F5EE' },
  { id: 'abracadabeauty', name: 'AbracadaBeauty', desc: 'Beauté & Soins', icon: '✨', iconBg: '#FBEAF0' },
  { id: 'abracadafashion', name: 'AbracadaFashion', desc: 'Mode & Luxe', icon: '👗', iconBg: '#F1EFE8' },
  { id: 'abracadatoys', name: 'AbracadaToys', desc: 'Jouets & Enfants', icon: '🧸', iconBg: '#FCEBEB' },
  { id: 'abracadazoo', name: 'AbracadaZoo', desc: 'Animaux & Nature', icon: '🐾', iconBg: '#EAF3DE' },
  { id: 'abracadalearn', name: 'AbracadaLearn', desc: 'Formation & Coaching', icon: '📚', iconBg: '#FAEEDA', smallDesc: true },
  { id: 'abracadahealth', name: 'AbracadaHealth', desc: 'Santé & Bien-être', icon: '❤️‍🩹', iconBg: '#FBEAF0' },
];

// ═══ POPUP WAITLIST ═══
const WaitlistPopup = ({ univers, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      setError('Remplis tous les champs');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/univers/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim().toLowerCase(),
          univers_id: univers.id,
          univers_name: univers.name,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Une erreur est survenue');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl max-w-sm w-full p-5 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            data-testid="waitlist-close-btn"
          >
            <X size={20} />
          </button>

          {success ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <Check size={28} className="text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                C'est noté !
              </h3>
              <p className="text-gray-600 text-sm">
                On t'avertit dès l'ouverture de <strong style={{ color: univers.nameColor }}>{univers.name}</strong>
              </p>
              <button
                onClick={onClose}
                className="mt-5 px-5 py-2.5 bg-[#E8650A] text-white rounded-xl font-semibold text-sm"
                data-testid="waitlist-success-close"
              >
                Fermer
              </button>
            </div>
          ) : (
            <>
              <div 
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 text-xl"
                style={{ background: univers.iconBg }}
              >
                {univers.icon}
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                Rejoins les premiers
              </h3>
              <p className="text-gray-500 text-xs mb-4">
                Sois averti dès l'ouverture de <strong style={{ color: univers.nameColor }}>{univers.name}</strong>
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Ton prénom"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E8650A] focus:border-transparent"
                    data-testid="waitlist-firstname-input"
                  />
                </div>

                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ton@email.com"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E8650A] focus:border-transparent"
                    data-testid="waitlist-email-input"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-xs">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-[#E8650A] text-white rounded-xl font-semibold text-sm disabled:opacity-50"
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? 'Inscription...' : 'Me prévenir'}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══ COMPOSANT PRINCIPAL ═══
export default function UniversPage() {
  const navigate = useNavigate();
  const [selectedUnivers, setSelectedUnivers] = useState(null);

  return (
    <div className="univers-page" style={{ 
      background: '#F5F5F7', 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      margin: '0 auto',
      paddingBottom: '80px',
    }}>
      {/* ═══ HEADER + NAVIGATION MOBILE ═══ */}
      <div className="sm:hidden">
        <UnifiedHeader hideSearchOnMobile={true} />
        <MainNavigation />
      </div>
      {/* ═══ HEADER ═══ */}
      <div className="sm:block hidden" style={{
        background: '#1a2a4a',
        padding: '16px 16px 18px',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          background: 'rgba(232,101,10,0.15)',
          border: '1px solid rgba(232,101,10,0.3)',
          borderRadius: '20px',
          padding: '4px 12px',
          fontSize: '11px',
          color: '#E8650A',
          fontWeight: 600,
          marginBottom: '8px',
        }}>
          🌍 21 Univers · 1 Plateforme
        </div>
        <div style={{
          fontSize: 'clamp(18px, 5vw, 22px)',
          fontWeight: 800,
          color: '#fff',
          marginBottom: '3px',
          letterSpacing: '-0.3px',
        }}>
          Choisis ton univers
        </div>
        <div style={{
          fontSize: 'clamp(11px, 3vw, 13px)',
          color: 'rgba(255,255,255,0.5)',
          fontWeight: 500,
        }}>
          Entre dans le centre commercial virtuel qui te correspond
        </div>
      </div>
      
      {/* ═══ CONTENU MOBILE — UX SCROLL INTUITIF ═══ */}
      <div className="sm:hidden content-univers" style={{
        padding: '12px',
        paddingTop: '56px',
        background: '#F8FAFC',
        position: 'relative',
      }}>
        {/* ═══ BANDEAU BLEU — MESSAGE CLÉ UNIQUE ═══ */}
        <div className="univers-info-banner" style={{
          background: '#1a2a4a',
          borderRadius: '16px',
          padding: '14px 16px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '11px',
            fontWeight: 700,
            flexShrink: 0,
          }}>
            ✓
          </div>
          <div style={{
            fontSize: '14px',
            lineHeight: 1.4,
          }}>
            <span style={{ color: '#fff', fontWeight: 700 }}>1 compte certifié = 21 univers.</span><br/>
            <span style={{ color: '#E8650A', fontWeight: 600 }}>Zéro faux profil. Que du vrai.</span>
          </div>
        </div>

        {/* ═══ CARD ABRACADABATI — IMMÉDIATEMENT VISIBLE ═══ */}
        <div 
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
            borderRadius: '18px',
            padding: '14px',
            marginBottom: '12px',
            border: '1.5px solid rgba(232,101,10,0.35)',
            boxShadow: '0 4px 16px rgba(13,27,46,0.08)',
          }}
          data-testid="univers-card-abracadabati"
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(34,197,94,0.08)',
            border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: '20px',
            padding: '4px 10px',
            fontSize: '10px',
            color: '#15803d',
            fontWeight: 600,
            marginBottom: '10px',
          }}>
            <span style={{
              width: '5px',
              height: '5px',
              background: '#22c55e',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'pulse 1.5s infinite',
            }} />
            Ouverture 1er Juillet 2026
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
          }}>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1a2a4a',
                marginBottom: '4px',
                letterSpacing: '-0.3px',
              }}>
                AbracadaBati
              </h2>
              <p style={{
                fontSize: '12px',
                color: '#374151',
                lineHeight: 1.4,
              }}>
                Artisans certifiés, marketplace BTP, réseau pro
              </p>
            </div>
            <button 
              onClick={() => navigate('/')}
              style={{
                background: '#E8650A',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 18px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                boxShadow: '0 4px 16px rgba(232,101,10,0.25)',
                minHeight: '44px',
              }}
              data-testid="univers-enter-btn"
            >
              Entrer →
            </button>
          </div>
        </div>

        {/* ═══ GRILLE DES UNIVERS — SCROLL INTUITIF ═══ */}
        <div className="univers-grid univers-fade-bottom" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px',
          marginTop: '8px',
          paddingBottom: '80px',
        }}>
          {UNIVERS_BIENTOT.map((univers) => (
            <div
              key={univers.id}
              onClick={() => setSelectedUnivers(univers)}
              className="univers-card"
              style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '12px 10px',
                border: '1px solid #F3F4F6',
                cursor: 'pointer',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '110px',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(15,23,42,0.04)',
              }}
              data-testid={`univers-card-${univers.id}`}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                marginBottom: '8px',
                background: `${univers.iconBg}`,
                boxShadow: `0 3px 10px ${univers.iconBg}40`,
              }}>
                {univers.icon}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#1F2937',
                marginBottom: '3px',
                lineHeight: 1.2,
              }}>
                {univers.name}
              </div>
              <div style={{
                fontSize: '10px',
                color: '#6B7280',
                lineHeight: 1.3,
                marginBottom: '6px',
              }}>
                {univers.desc}
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                padding: '3px 8px',
                fontSize: '9px',
                fontWeight: 600,
                background: 'rgba(232,101,10,0.08)',
                color: '#C2410C',
                border: '1px solid rgba(232,101,10,0.15)',
              }}>
                Bientôt
              </div>
            </div>
          ))}
          
          {/* UNIVERS 2027 - dans la même grille */}
          {UNIVERS_2027.map((univers) => (
            <div
              key={univers.id}
              className="univers-card"
              style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '12px 10px',
                border: '1px solid #F3F4F6',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: '110px',
                justifyContent: 'center',
                boxShadow: '0 2px 10px rgba(15,23,42,0.04)',
                opacity: 0.7,
              }}
              data-testid={`univers-card-${univers.id}`}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                marginBottom: '6px',
                background: univers.iconBg,
              }}>
                {univers.icon}
              </div>
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#6B7280',
                marginBottom: '3px',
                lineHeight: 1.2,
              }}>
                {univers.name}
              </div>
              <div style={{
                fontSize: '10px',
                color: '#9CA3AF',
                lineHeight: 1.3,
                marginBottom: '6px',
              }}>
                {univers.desc}
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                padding: '3px 8px',
                fontSize: '9px',
                fontWeight: 600,
                background: '#F3F4F6',
                color: '#6B7280',
              }}>
                2027
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ POPUP WAITLIST ═══ */}
      {selectedUnivers && (
        <WaitlistPopup 
          univers={selectedUnivers} 
          onClose={() => setSelectedUnivers(null)} 
        />
      )}

      {/* ═══ ANIMATION PULSE + STYLES SCROLL ═══ */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        
        /* ═══ GRADIENT DE CONTINUITÉ POUR INDIQUER LE SCROLL ═══ */
        @media (max-width: 639px) {
          .content-univers::after {
            content: "";
            position: fixed;
            bottom: 70px;
            left: 0;
            width: 100%;
            height: 40px;
            background: linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.95) 100%);
            pointer-events: none;
            z-index: 5;
          }
        }
        
        /* ═══ RESPONSIVE GRID POUR TRÈS PETITS ÉCRANS ═══ */
        @media (max-width: 359px) {
          .univers-grid-bientot,
          .univers-grid-2027 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
}
